/**
 * Currency utilities for BIRHAUS Financial components
 * SEPRELAD compliant and Paraguay-first
 */

import type { CurrencyCode, Currency, CurrencyFormatOptions } from '../types/financial'

/**
 * Supported currencies with Paraguay-first configuration
 */
export const SUPPORTED_CURRENCIES: Record<CurrencyCode, Currency> = {
  PYG: {
    code: 'PYG',
    symbol: '₲',
    nameEs: 'Guaraní paraguayo',
    nameEn: 'Paraguayan guaraní',
    decimals: 0, // Guaraní doesn't use decimal places
    thousandSeparator: '.',
    decimalSeparator: ','
  },
  USD: {
    code: 'USD',
    symbol: '$',
    nameEs: 'Dólar estadounidense',
    nameEn: 'US dollar',
    decimals: 2,
    thousandSeparator: ',',
    decimalSeparator: '.'
  },
  EUR: {
    code: 'EUR',
    symbol: '€',
    nameEs: 'Euro',
    nameEn: 'Euro',
    decimals: 2,
    thousandSeparator: '.',
    decimalSeparator: ','
  },
  BRL: {
    code: 'BRL',
    symbol: 'R$',
    nameEs: 'Real brasileño',
    nameEn: 'Brazilian real',
    decimals: 2,
    thousandSeparator: '.',
    decimalSeparator: ','
  },
  ARS: {
    code: 'ARS',
    symbol: '$',
    nameEs: 'Peso argentino',
    nameEn: 'Argentine peso',
    decimals: 2,
    thousandSeparator: '.',
    decimalSeparator: ','
  }
}

/**
 * Exchange rates (mock data - in production, fetch from API)
 * Base currency: USD
 */
export const EXCHANGE_RATES: Record<CurrencyCode, number> = {
  USD: 1,
  PYG: 7300, // 1 USD = 7300 PYG (approximate)
  EUR: 0.85, // 1 USD = 0.85 EUR (approximate)
  BRL: 5.2,  // 1 USD = 5.2 BRL (approximate)
  ARS: 800   // 1 USD = 800 ARS (approximate)
}

/**
 * SEPRELAD reporting thresholds in USD
 */
export const SEPRELAD_THRESHOLDS = {
  CASH_OPERATION: 10000, // USD 10,000 for cash operations
  SUSPICIOUS_OPERATION: 5000, // USD 5,000 for suspicious operations
  DAILY_CUMULATIVE: 10000 // USD 10,000 cumulative daily
}

/**
 * Format currency amount according to locale and currency rules
 */
export function formatCurrency(
  amount: number,
  options: Partial<CurrencyFormatOptions> = {}
): string {
  const {
    currency = 'PYG',
    locale = 'es-PY',
    showSymbol = true,
    showCode = false,
    minimumFractionDigits,
    maximumFractionDigits
  } = options

  const currencyInfo = SUPPORTED_CURRENCIES[currency]
  if (!currencyInfo) {
    throw new Error(`Unsupported currency: ${currency}`)
  }

  // Handle special case for Guaraní (no decimals)
  const fracDigits = currency === 'PYG' ? 0 : (minimumFractionDigits ?? currencyInfo.decimals)
  const maxFracDigits = currency === 'PYG' ? 0 : (maximumFractionDigits ?? currencyInfo.decimals)

  try {
    // Use Intl.NumberFormat for proper localization
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: fracDigits,
      maximumFractionDigits: maxFracDigits
    })

    let formatted = formatter.format(amount)

    // For Paraguay locale with PYG, ensure proper formatting
    if (currency === 'PYG' && locale.startsWith('es')) {
      // Format: ₲ 1.234.567 (with spaces and dots as thousand separators)
      const numberPart = Math.abs(amount).toLocaleString('es-PY')
      const sign = amount < 0 ? '-' : ''
      formatted = `${sign}${currencyInfo.symbol} ${numberPart}`
    }

    // Add currency code if requested
    if (showCode && !showSymbol) {
      const numberPart = Math.abs(amount).toLocaleString(locale, {
        minimumFractionDigits: fracDigits,
        maximumFractionDigits: maxFracDigits
      })
      const sign = amount < 0 ? '-' : ''
      formatted = `${sign}${numberPart} ${currency}`
    } else if (showCode && showSymbol) {
      formatted = `${formatted} ${currency}`
    }

    return formatted

  } catch (error) {
    // Fallback formatting if Intl.NumberFormat fails
    console.warn('Currency formatting failed, using fallback:', error)
    
    const numberPart = Math.abs(amount).toFixed(fracDigits)
    const sign = amount < 0 ? '-' : ''
    
    if (showSymbol) {
      return `${sign}${currencyInfo.symbol} ${numberPart}`
    } else if (showCode) {
      return `${sign}${numberPart} ${currency}`
    } else {
      return `${sign}${numberPart}`
    }
  }
}

/**
 * Parse currency string back to number
 */
export function parseCurrency(value: string, currency: CurrencyCode = 'PYG'): number | null {
  if (!value || typeof value !== 'string') {
    return null
  }

  const currencyInfo = SUPPORTED_CURRENCIES[currency]
  if (!currencyInfo) {
    return null
  }

  // Remove currency symbols and codes
  let cleanValue = value
    .replace(new RegExp(`\\${currencyInfo.symbol}`, 'g'), '')
    .replace(new RegExp(currency, 'g'), '')
    .trim()

  // Handle negative values
  const isNegative = cleanValue.startsWith('-')
  if (isNegative) {
    cleanValue = cleanValue.substring(1)
  }

  // Remove thousand separators and replace decimal separator
  if (currency === 'PYG') {
    // For Guaraní: remove dots (thousand separators)
    cleanValue = cleanValue.replace(/\./g, '')
  } else {
    // For other currencies: handle both comma and dot separators
    // First remove thousand separators, then handle decimal
    if (cleanValue.includes(',') && cleanValue.includes('.')) {
      // Determine which is decimal separator (last occurrence)
      const lastComma = cleanValue.lastIndexOf(',')
      const lastDot = cleanValue.lastIndexOf('.')
      
      if (lastDot > lastComma) {
        // Dot is decimal separator
        cleanValue = cleanValue.replace(/,/g, '').replace('.', '.')
      } else {
        // Comma is decimal separator
        cleanValue = cleanValue.replace(/\./g, '').replace(',', '.')
      }
    } else if (cleanValue.includes(',')) {
      // Only comma - could be thousand or decimal separator
      const commaCount = (cleanValue.match(/,/g) || []).length
      if (commaCount === 1 && cleanValue.split(',')[1].length <= 2) {
        // Likely decimal separator
        cleanValue = cleanValue.replace(',', '.')
      } else {
        // Thousand separator
        cleanValue = cleanValue.replace(/,/g, '')
      }
    }
  }

  // Parse the number
  const parsed = parseFloat(cleanValue)
  
  if (isNaN(parsed)) {
    return null
  }

  return isNegative ? -parsed : parsed
}

/**
 * Convert amount from one currency to another
 */
export function convertCurrency(
  amount: number,
  fromCurrency: CurrencyCode,
  toCurrency: CurrencyCode
): number {
  if (fromCurrency === toCurrency) {
    return amount
  }

  // Convert to USD first, then to target currency
  const usdAmount = amount / EXCHANGE_RATES[fromCurrency]
  const convertedAmount = usdAmount * EXCHANGE_RATES[toCurrency]

  // Round based on target currency decimals
  const targetDecimals = SUPPORTED_CURRENCIES[toCurrency].decimals
  return Math.round(convertedAmount * Math.pow(10, targetDecimals)) / Math.pow(10, targetDecimals)
}

/**
 * Check if amount exceeds SEPRELAD reporting thresholds
 */
export function checkSEPRELADThreshold(
  amount: number,
  currency: CurrencyCode,
  operationType: 'cash' | 'suspicious' | 'cumulative' = 'cash'
): { requiresReport: boolean; thresholdUSD: number; amountUSD: number } {
  const amountUSD = convertCurrency(amount, currency, 'USD')
  
  let thresholdUSD: number
  switch (operationType) {
    case 'cash':
      thresholdUSD = SEPRELAD_THRESHOLDS.CASH_OPERATION
      break
    case 'suspicious':
      thresholdUSD = SEPRELAD_THRESHOLDS.SUSPICIOUS_OPERATION
      break
    case 'cumulative':
      thresholdUSD = SEPRELAD_THRESHOLDS.DAILY_CUMULATIVE
      break
    default:
      thresholdUSD = SEPRELAD_THRESHOLDS.CASH_OPERATION
  }

  return {
    requiresReport: amountUSD >= thresholdUSD,
    thresholdUSD,
    amountUSD
  }
}

/**
 * Get currency info
 */
export function getCurrencyInfo(currency: CurrencyCode): Currency {
  return SUPPORTED_CURRENCIES[currency]
}

/**
 * Validate currency amount for display and processing
 */
export function validateCurrencyAmount(
  amount: number,
  currency: CurrencyCode
): { valid: boolean; errors: string[] } {
  const errors: string[] = []

  // Check if amount is a valid number
  if (!Number.isFinite(amount)) {
    errors.push('Invalid number format')
    return { valid: false, errors }
  }

  // Check for reasonable limits
  if (Math.abs(amount) > 1000000000000) { // 1 trillion limit
    errors.push('Amount exceeds maximum allowed value')
  }

  // Check decimal places for currency
  const currencyInfo = SUPPORTED_CURRENCIES[currency]
  if (currencyInfo) {
    const decimalPlaces = (amount.toString().split('.')[1] || '').length
    if (decimalPlaces > currencyInfo.decimals) {
      errors.push(`Too many decimal places for ${currency} (max: ${currencyInfo.decimals})`)
    }
  }

  // Check for SEPRELAD thresholds (warnings, not errors)
  const seprelad = checkSEPRELADThreshold(amount, currency)
  if (seprelad.requiresReport) {
    // This is informational, not an error
    console.info(`Amount exceeds SEPRELAD reporting threshold: ${seprelad.amountUSD} USD`)
  }

  return {
    valid: errors.length === 0,
    errors
  }
}

/**
 * Format currency for form inputs (removes symbols, keeps numbers)
 */
export function formatCurrencyForInput(
  amount: number,
  currency: CurrencyCode = 'PYG'
): string {
  const currencyInfo = SUPPORTED_CURRENCIES[currency]
  if (!currencyInfo) {
    return amount.toString()
  }

  if (currency === 'PYG') {
    // For Guaraní: no decimals, dot as thousand separator
    return amount.toLocaleString('es-PY', {
      useGrouping: true,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })
  } else {
    // For other currencies: include decimals
    return amount.toLocaleString('es-PY', {
      useGrouping: true,
      minimumFractionDigits: currencyInfo.decimals,
      maximumFractionDigits: currencyInfo.decimals
    })
  }
}